import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { v4 } from "uuid";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { DateInput } from "../../components/DateInput/DateInput";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { PdfPreview } from "../../components/PdfPreview/PDFPreview";
import { getPreviewOwnershipReproDocumentTemplate } from "../../document-templates/PreviewOwnershipReproDocumentTemplate";
import { OwnershipDocument } from "../../models/OwnershipDocument";
import { Warehouse } from "../../models/Warehouse";
import { Partner } from "../../models/Partner";
import { Product, ProductType } from "../../models/Product";
import { RoleType } from "../../models/Role";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { Utility } from "../../core/Utility";
import { activeUser } from "../../services/AuthService";
import { useApplicationContext } from "../../application/ApplicationContext";
import { getAllWarehouses } from "../../services/WarehouseService";
import { getAllProducts } from "../../services/ProductService";
import { getAllPartners } from "../../services/PartnerService";
import {
  getAllOwnershipDocuments,
  deleteOneOwnershipDocument,
} from "../../services/OwnershipDocumentService";
import "./OwnershipReproDocumentsPage.scss";

export const OwnershipReproDocumentsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useRef<any>(null);

  const { state } = useApplicationContext();
  const businessYear = state.businessYear ?? new Date().getFullYear();
  const yearStart = new Date(businessYear, 0, 1);
  const yearEnd = new Date(businessYear, 11, 31);
  const getDefaultRangeForBusinessYear = () => {
    const today = new Date();
    if (businessYear === today.getFullYear()) {
      // Za tekuću godinu, postavi datume od 1. januara do danas
      const from = new Date(today.getFullYear(), 0, 1);
      const to = today;
      return { from, to };
    } else {
      // Za prethodne godine, postavi ceo opseg te godine
      return { from: yearStart, to: yearEnd };
    }
  };

  const [dateFrom, setDateFrom] = useState<Date>(
    getDefaultRangeForBusinessYear().from
  );
  const [dateTo, setDateTo] = useState<Date>(
    getDefaultRangeForBusinessYear().to
  );

  const [processing, setProcessing] = useState<boolean>(false);
  const [warehousesApi, setWarehousesApi] = useState<Warehouse[]>([]);
  const [productsApi, setProductsApi] = useState<Product[]>([]);
  const [partnersApi, setPartnersApi] = useState<Partner[]>([]);
  const [ownershipDocumentsApi, setOwnershipDocumentsApi] = useState<
    OwnershipDocument[]
  >([]);

  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse>(
    {} as Warehouse
  );
  const [selectedPartner, setSelectedPartner] = useState<Partner>(
    {} as Partner
  );
  const [selectedProduct, setSelectedProduct] = useState<Product>(
    {} as Product
  );
  const [deleteConfirmVisible, setDeleteConfirmVisible] =
    useState<boolean>(false);
  const [inputToDelete, setInputToDelete] = useState<string>("");
  const [ownershipDocumentToPrint, setOwnershipDocumentToPrint] = useState<
    OwnershipDocument[]
  >([]);
  const [pdfData, setPdfData] = useState<any>(
    getPreviewOwnershipReproDocumentTemplate(
      ownershipDocumentToPrint,
      selectedWarehouse?.name,
      [],
      dateFrom,
      dateTo
    )
  );

  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);

  const downloadPdfDocument = useRef<any>(null);
  const printPdfDocument = useRef<any>(null);

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  const refreshAll = async () => {
    setProcessing(true);
    try {
      const [wResp, pResp, partnerResp, odResp] = await Promise.all([
        getAllWarehouses(),
        getAllProducts(),
        getAllPartners(),
        getAllOwnershipDocuments(),
      ]);

      [wResp, pResp, partnerResp, odResp].forEach(ensureSuccess);

      setWarehousesApi(wResp?.data?.result ?? []);
      setProductsApi(pResp?.data?.result ?? []);
      setPartnersApi(partnerResp?.data?.result ?? []);
      setOwnershipDocumentsApi(odResp?.data?.result ?? []);
    } finally {
      setProcessing(false);
    }
  };

  const refreshOwnershipDocuments = async () => {
    setProcessing(true);
    try {
      const odResp = await getAllOwnershipDocuments();
      ensureSuccess(odResp);
      setOwnershipDocumentsApi(odResp?.data?.result ?? []);
    } finally {
      setProcessing(false);
    }
  };

  useEffect(() => {
    refreshAll()
      .then(() => formatOwnershipDocumentToPrint())
      .catch((e: any) => {
        toast.current?.show({
          severity: "error",
          detail: e?.message ?? String(t("Greška pri učitavanju podataka")),
          life: 3500,
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const { from, to } = getDefaultRangeForBusinessYear();
    setDateFrom(from);
    setDateTo(to);
    refreshOwnershipDocuments()
      .then(() => formatOwnershipDocumentToPrint())
      .catch((e: any) => {
        toast.current?.show({
          severity: "error",
          detail:
            e?.response?.data?.message ??
            e?.message ??
            String(t("Greška pri učitavanju vlasničkih listova")),
          life: 3500,
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessYear]);

  useEffect(() => {
    formatOwnershipDocumentToPrint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ownershipDocumentsApi,
    warehousesApi,
    productsApi,
    partnersApi,
    selectedWarehouse,
    selectedPartner,
    selectedProduct,
    dateFrom,
    dateTo,
  ]);

  useEffect(() => {
    formatPdfData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ownershipDocumentToPrint,
    selectedWarehouse,
    dateFrom,
    dateTo,
    productsApi,
  ]);

  const {
    control,
    formState: { errors },
  } = useForm({
    mode: "all",
  });

  const safeParseProducts = (json?: string): Product[] => {
    try {
      const parsed = JSON.parse(json || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const formatOwnershipDocumentToPrint = () => {
    const warehouse = warehousesApi.find((x) => x.id == selectedWarehouse.id);
    const partner = partnersApi.find((x) => x.id == selectedPartner.id);
    const product = productsApi.find((x) => x.id == selectedProduct.id);

    const warehouseName = warehouse?.name;
    const partnerId = partner?.id;
    const productId = product?.id;

    const dateFromMs = new Date(dateFrom).getTime();
    const dateToMs = new Date(dateTo).getTime() + 1000 * 60 * 60 * 24; // kraj dana

    const filtered: OwnershipDocument[] = [];

    for (const doc of ownershipDocumentsApi) {
      const docDateMs = new Date(doc?.date as any).getTime();

      const baseMatch =
        (warehouse ? doc.warehouse?.name === warehouseName : true) &&
        (partner ? doc.previousOwner?.id === partnerId : true) &&
        doc.srps == 0 &&
        docDateMs > dateFromMs &&
        docDateMs <= dateToMs;

      if (!baseMatch) continue;

      if (productId) {
        const reproProducts = safeParseProducts(
          doc.repromaterialProductsJSON as any
        );
        const hasProduct = reproProducts.some((p: any) => p?.id === productId);
        if (!hasProduct) continue;
      }

      filtered.push(doc);
    }

    filtered.sort((a, b) =>
      new Date(a?.date as any).getTime() < new Date(b?.date as any).getTime()
        ? 1
        : -1
    );

    setOwnershipDocumentToPrint(filtered);
  };

  const formatPdfData = () => {
    const allProductNames: string[] = ownershipDocumentToPrint.map((doc) => {
      const reproProducts = safeParseProducts(
        doc.repromaterialProductsJSON as any
      );
      const names = reproProducts
        .map((p: any) => productsApi.find((x) => x.id == p?.id)?.name)
        .filter(Boolean)
        .join(", ");
      return names;
    });

    const newPdf = getPreviewOwnershipReproDocumentTemplate(
      ownershipDocumentToPrint,
      selectedWarehouse?.name,
      allProductNames,
      dateFrom,
      dateTo
    );
    setPdfData(newPdf);
  };

  const handleWarehouseChange = (prop: string, value: any) => {
    const wh = warehousesApi.find((x) => x.id == value) as Warehouse;
    setSelectedWarehouse(wh ?? ({} as Warehouse));
  };

  const handlePartnerChange = (prop: string, value: any) => {
    const partner = partnersApi.find((x) => x.id == value) as Partner;
    setSelectedPartner(partner ?? ({} as Partner));
  };

  const handleProductChange = (prop: string, value: any) => {
    const prod = productsApi.find((x) => x.id == value) as Product;
    setSelectedProduct(prod ?? ({} as Product));
  };

  const onDateFromChange = (_property: string, value: Date | null) => {
    setDateFrom(value as Date);
  };

  const onDateToChange = (_property: string, value: Date | null) => {
    setDateTo(value as Date);
  };

  const acceptDelete = async () => {
    try {
      const resp = await deleteOneOwnershipDocument(inputToDelete);
      ensureSuccess(resp);

      setDeleteConfirmVisible(false);
      setInputToDelete("");

      await refreshAll();

      toast.current?.show({
        severity: "success",
        detail: t("Vlasnički list je obrisan"),
        life: 3000,
      });
    } catch (e: any) {
      toast.current?.show({
        severity: "error",
        detail:
          e?.response?.data?.message ??
          e?.message ??
          String(t("Greška pri brisanju")),
        life: 3500,
      });
    }
  };

  const exportExcel = () => {
    const excelOwnershipDocument = ownershipDocumentToPrint.map(
      (item: OwnershipDocument) => {
        const reproParsedProducts = safeParseProducts(
          item?.repromaterialProductsJSON as any
        );
        let productNames = "";
        for (const oneProduct of reproParsedProducts) {
          productNames +=
            productsApi.find((item) => item.id == oneProduct.id)?.name + ", ";
        }
        if (productNames.length > 2)
          productNames = productNames.substring(0, productNames.length - 2);
        return {
          BrojVlLista: item.ownerDocNumberFormatted,
          Datum: Utility.formatDate(new Date(item.date as Date)),
          Magacin: item.warehouse.name,
          PrethodniVlasnik: item.previousOwner?.name,
          NoviVlasnik: item.newOwner?.name,
          Roba: productNames,
        };
      }
    );
    const excelObject = [
      {
        BrojVlLista: "",
        Datum: "",
        Magacin: "",
        PrethodniVlasnik: "",
        NoviVlasnik: "",
        Roba: "",
      },
      ...excelOwnershipDocument,
    ];
    import("xlsx").then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(excelObject);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
      const excelBuffer = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      saveAsExcelFile(
        excelBuffer,
        `Vlasnički listovi repromaterijala ${Utility.formatDate(new Date())}`
      );
    });
  };

  const saveAsExcelFile = (buffer: any, fileName: any) => {
    import("file-saver").then((module: any) => {
      if (module && module.default) {
        let EXCEL_TYPE =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        let EXCEL_EXTENSION = ".xlsx";
        const data = new Blob([buffer], {
          type: EXCEL_TYPE,
        });
        module.default.saveAs(data, fileName + EXCEL_EXTENSION);
      }
    });
  };

  const renderProducts = (rowData: OwnershipDocument) => {
    const reproParsedProducts = safeParseProducts(
      rowData.repromaterialProductsJSON as any
    );
    const productNames = reproParsedProducts
      .map((p: any) => productsApi.find((item) => item.id === p.id)?.name)
      .filter(Boolean)
      .join(", ");
    return <span>{productNames}</span>;
  };

  const renderActions = (rowData: OwnershipDocument) => {
    return (
      <div className="action-buttons">
        <ButtonInput
          addStyle={{ border: "none", background: "#198754" }}
          labelText={""}
          icon="bi bi-pencil"
          onButtonClick={() =>
            navigate(`/ownership-repro-document/${rowData.id}`)
          }
          disabled={activeUser?.role?.name === RoleType.Director}
        />
        <ButtonInput
          addStyle={{ border: "none", background: "#198754" }}
          labelText={""}
          icon="bi bi-trash"
          onButtonClick={() => {
            setInputToDelete(rowData.id);
            setDeleteConfirmVisible(true);
          }}
          disabled={activeUser?.role?.name === RoleType.Director}
        />
      </div>
    );
  };

  const resetFilters = () => {
    const { from, to } = getDefaultRangeForBusinessYear();
    setDateFrom(from);
    setDateTo(to);
    setSelectedWarehouse({} as Warehouse);
    setSelectedPartner({} as Partner);
    setSelectedProduct({} as Product);
  };

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="table-wrapper">
        <div className="table-header">
          <div className="header-left">
            <i
              className="bi bi-file-text header-title-icon"
              style={{
                fontSize: 14,
                marginLeft: "1rem",
              }}
            ></i>
            <span className="header-title">
              {t("Vlasnički listovi repromaterijala")}
            </span>
            <DropdownInput
              id="warehouse"
              value={selectedWarehouse?.id}
              onValueChange={handleWarehouseChange}
              optionValue="id"
              optionLabel="name"
              options={warehousesApi.filter((item) => item.virtual !== true)}
              labelText=""
              placeholderText={t("Magacin")}
              control={control}
              errors={errors}
            />
            <span className="header-elements">{t("od")}</span>
            <DateInput
              id="dateFrom"
              value={dateFrom}
              onValueChange={onDateFromChange}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
            />
            <span className="header-elements">{t("do")}</span>
            <DateInput
              id="dateTo"
              value={dateTo}
              onValueChange={onDateToChange}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
            />
            <span className="header-elements">{t("za")}</span>
            <DropdownInput
              id="selectedProduct"
              value={selectedProduct?.id}
              onValueChange={handleProductChange}
              optionValue="id"
              optionLabel="name"
              options={productsApi.filter(
                (item) => item.type == ProductType.RawMaterial
              )}
              filter={true}
              labelText=""
              placeholderText={t("Proizvod")}
              control={control}
              errors={errors}
            />
            <span className="header-elements">{t("dobavljač")}</span>
            <DropdownInput
              id="selectedPartner"
              value={selectedPartner?.id}
              onValueChange={handlePartnerChange}
              optionValue="id"
              optionLabel="name"
              options={partnersApi}
              filter={true}
              labelText=""
              placeholderText={t("Dobavljač")}
              control={control}
              errors={errors}
            />
            <ButtonInput
              addStyle={{
                border: "none",
                background: "#ffffff",
                color: "#6C757D",
                margin: "0 0 0 1rem",
              }}
              labelText={"reset"}
              icon="bi bi-arrow-repeat"
              onButtonClick={resetFilters}
            />
          </div>
          <div className="header-right">
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={""}
              icon="bi bi-file-earmark-pdf"
              onButtonClick={() => {
                if (downloadPdfDocument.current) {
                  // @ts-ignore
                  downloadPdfDocument.current();
                }
              }}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
              }}
              labelText={""}
              icon="bi bi-printer"
              onButtonClick={() => {
                if (printPdfDocument.current) {
                  // @ts-ignore
                  printPdfDocument.current();
                }
              }}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                marginLeft: "5px",
              }}
              labelText={""}
              icon="bi bi-file-earmark-excel"
              onButtonClick={exportExcel}
            />
            <ButtonInput
              addStyle={{
                border: "none",
                background: "#ffffff",
                color: "#6C757D",
                margin: "0 1rem 0 2rem",
              }}
              labelText={t("Nov vlasnički list")}
              icon="bi bi-file-text"
              onButtonClick={() =>
                navigate(`/ownership-repro-document/${v4()}`)
              }
            />
          </div>
        </div>
        {processing ? (
          <ProgressSpinner />
        ) : (
          <div className="filtered-table">
            <DataTable
              value={ownershipDocumentToPrint}
              showGridlines
              stripedRows
              emptyMessage={t("Nema podataka za prikaz")}
              paginator
              first={first}
              rows={rows}
              onPage={(e) => {
                setFirst(e.first);
                setRows(e.rows);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
              currentPageReportTemplate=""
            >
              <Column
                field="ownerDocNumberFormatted"
                header={t("Broj vl. lista")}
              />
              <Column
                field="date"
                header={t("Datum")}
                body={(rowData) => Utility.formatDate(new Date(rowData.date))}
              />
              <Column
                field="warehouse.name"
                header={t("Magacin")}
                body={(rowData) => rowData.warehouse?.name.toUpperCase()}
              />
              <Column
                field="previousOwner.name"
                header={t("Prethodni vlasnik")}
                body={(rowData) => rowData.previousOwner?.name}
              />
              <Column
                field="newOwner.name"
                header={t("Novi vlasnik")}
                body={(rowData) => rowData.newOwner?.name}
              />
              <Column header={t("Naziv robe")} body={renderProducts} />
              <Column header={t("Opcije")} body={renderActions} />
            </DataTable>
          </div>
        )}
        <div className="document-preview">
          <PdfPreview
            pdfDefinition={pdfData}
            downloadDocument={downloadPdfDocument}
            printDocument={printPdfDocument}
            documentName={`${t(
              "Vlasnički listovi repromaterijala"
            )}_${Utility.formatDate(new Date())}.pdf`}
          />
        </div>
      </div>
      <ConfirmDialog
        visible={deleteConfirmVisible}
        onHide={() => setDeleteConfirmVisible(false)}
        message={t("Da li ste sigurni da želite da obrišete vlasnički list?")}
        header={t("Potvrda brisanja vlasničkog lista")}
        icon="bi bi-exclamation-circle"
        acceptLabel={`${t("Da")}`}
        acceptClassName="accept-button"
        accept={acceptDelete}
        rejectLabel={`${t("Ne")}`}
        rejectClassName="reject-button"
      />
    </PageLayout>
  );
};
